import { useMemo } from 'react';
import { getText } from 'i18n/intl';
import { fieldsSelector } from 'slices/field.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import {
  type DescriptionFieldSetting,
  type ReservationInfoProperty,
  isFreeTextSetting,
  isObjectFieldSetting,
  isReservationFieldSetting,
  isReservationInfoSetting,
} from '@timeedit/calendar-integration-api-contract';
import { TEInput } from '@timeedit/ui-components';
import { Space, Typography } from '@timeedit/ui-components/antd';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';

const intlPrefix = 'integration.calendar.sync_settings';

type ReservationValueInputProps = {
  value: DescriptionFieldSetting;
  onChange: (val: DescriptionFieldSetting) => void;
};

const ReservationValueInput = ({
  value,
  onChange,
}: ReservationValueInputProps) => {
  const fields = useAppSelector(fieldsSelector);
  const objectTypes = useAppSelector(selectObjectTypesList);

  const fieldOptions = useMemo(() => {
    return fields.map((field) => ({
      value: field.id,
      label: field.name,
      extId: field.extId,
    }));
  }, [fields]);

  const objectTypeOptions = useMemo(
    () =>
      objectTypes.map((type) => ({
        value: type.id,
        label: type.name,
        extId: type.extId,
      })),
    [objectTypes]
  );

  const filterFieldOptions = useMemo(() => {
    if (isObjectFieldSetting(value)) {
      const objectType = objectTypes.find(
        (type) => type.id === value.objectTypeId
      );
      const fieldIds = [
        ...(objectType?.fields || []),
        ...(objectType?.parentFields || []),
      ];
      return fieldOptions.filter((option) => fieldIds.includes(option.value));
    }

    return [];
  }, [fieldOptions, value, objectTypes]);

  const reservationInfoOptions: (DefaultOptionType & {
    value: ReservationInfoProperty;
  })[] = [
    {
      value: 'id',
      label: getText(
        `${intlPrefix}.form.sync_mode.te_out.subject.field_type.reservation_id`
      ),
    },
    {
      value: 'status_c',
      label: getText(
        `${intlPrefix}.form.sync_mode.te_out.subject.field_type.reservation_status_c`
      ),
    },
  ];

  if (isFreeTextSetting(value)) {
    return (
      <TEInput.TextArea
        value={value.text}
        onChange={(val) => onChange({ ...value, text: val })}
      />
    );
  } else if (isObjectFieldSetting(value)) {
    return (
      <Space direction="vertical" style={{ width: '100%' }}>
        <TEInput.Select
          label="Object"
          value={value.objectTypeId}
          options={objectTypeOptions}
          onChange={(val) =>
            onChange({ ...value, objectTypeId: val && Number(val) })
          }
          showSearch
          optionFilterProp="label"
          optionRender={optionRender}
          size="small"
        />
        <TEInput.Select
          label="Field"
          value={value.fieldId}
          options={filterFieldOptions}
          onChange={(val) =>
            onChange({ ...value, fieldId: val && Number(val) })
          }
          showSearch
          optionFilterProp="label"
          optionRender={optionRender}
          size="small"
        />
      </Space>
    );
  } else if (isReservationFieldSetting(value)) {
    return (
      <TEInput.Select
        label="Field"
        value={value.fieldId}
        options={fieldOptions}
        onChange={(val) => onChange({ ...value, fieldId: val && Number(val) })}
        showSearch
        optionFilterProp="label"
        optionRender={optionRender}
        size="small"
      />
    );
  } else if (isReservationInfoSetting(value)) {
    return (
      <TEInput.Select
        label="Field"
        value={value.propertyName}
        options={reservationInfoOptions}
        onChange={(val) => onChange({ ...value, propertyName: val })}
        showSearch
        optionFilterProp="label"
        optionRender={optionRender}
        size="small"
      />
    );
  }
  return null;
};

export default ReservationValueInput;

const optionRender = (option: DefaultOptionType) => {
  return (
    <Space>
      {option.label}
      {option.data.extId ? (
        <Typography.Text code>{option.data.extId}</Typography.Text>
      ) : null}
    </Space>
  );
};
